<template>
  <FinderCard data-test="finder-price-card" :title="criterion.title">
    <div class="flex flex-wrap gap-8">
      <RevChip
        v-for="preset in criterion.presets"
        :key="preset.id"
        class="body-1"
        :data-test="`budget-preset-${preset.id}`"
        :label="
          i18n.price(preset.maxValue, {
            currency: defaultCurrency,
            maximumFractionDigits: 0,
          })
        "
        :model-value="isPresetActive(preset.id)"
        size="medium"
        @click="togglePreset(preset.id)"
      />
    </div>

    <div v-if="priceFacet" class="mt-12">
      <div class="relative h-[92px]">
        <Graph
          class="absolute inset-x-0 top-0 h-[72px]"
          data-test="budget-graph"
          :items="priceFacet.values"
          :max-selected="priceRange[1]"
          :min-selected="priceRange[0]"
          :step="priceFacet.step"
        />
        <RevRange
          v-model="priceRange"
          class="absolute inset-x-0 bottom-0 h-[28px]"
          data-test="budget-range"
          :disabled="!isReady"
          :label="[minLabel, maxLabel]"
          :max="priceFacet.max"
          :step="priceFacet.step"
        />
      </div>
      <div class="mt-16 flex justify-between gap-8">
        <PriceInput
          id="budget-min-price"
          v-model="priceRange[0]"
          class="w-96"
          :label="minLabel"
          :max="priceFacet.max"
          :min="0"
        />
        <PriceInput
          id="budget-max-price"
          v-model="priceRange[1]"
          class="w-96"
          :label="maxLabel"
          :max="priceFacet.max"
          :min="0"
        />
      </div>
    </div>
  </FinderCard>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'

import type { Currency } from '@backmarket/http-api/src/standards/Currency'
import Graph from '@backmarket/nuxt-layer-recommendation/filtering/Graph.vue'
import PriceInput from '@backmarket/nuxt-layer-recommendation/filtering/PriceInput.vue'
import type { UiPriceFacet } from '@backmarket/nuxt-layer-recommendation/filtering/types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { RevChip } from '@ds/components/Chip'
import { RevRange } from '@ds/components/Range'
import { watchDebounced } from '@vueuse/core'

import { isBudgetPresetSelected } from '../../../../composables/useFinder/isBudgetPresetSelected'
import type { FinderBudgetCriterion } from '../../../../composables/useFinder/useFinder.types'
import FinderCard from '../FinderCard/FinderCard.vue'

import translations from './FinderPriceTile.translations'

const props = defineProps<{
  criterion: FinderBudgetCriterion
  priceFacet?: UiPriceFacet
}>()

const model = defineModel<[number, number] | undefined>()

const i18n = useI18n()
const { defaultCurrency } = useMarketplace().market

const max = computed(() => props.priceFacet?.max ?? 0)
const priceRange = ref<[number, number]>(model.value ?? [0, max.value])
const isReady = ref(false)

watch(
  model,
  (newModelValue) => {
    priceRange.value = newModelValue ?? [0, max.value]
  },
  { deep: true },
)

watch(max, (newMax) => {
  if (!model.value) {
    priceRange.value = [0, newMax]
  }
})

watchDebounced(
  priceRange,
  (newRange) => {
    const isFullRange = newRange[0] === 0 && newRange[1] === max.value
    model.value = isFullRange ? undefined : newRange
  },
  { deep: true, debounce: 500 },
)

onMounted(() => {
  isReady.value = true
})

const minLabel = computed(() =>
  i18n(translations.minPrice, { currency: defaultCurrency as Currency }),
)
const maxLabel = computed(() =>
  i18n(translations.maxPrice, { currency: defaultCurrency as Currency }),
)

function isPresetActive(presetId: string): boolean {
  const preset = props.criterion.presets.find((p) => p.id === presetId)
  if (!preset) return false

  return isBudgetPresetSelected(model.value, preset)
}

function togglePreset(presetId: string): void {
  const preset = props.criterion.presets.find((p) => p.id === presetId)
  if (!preset) return

  if (isPresetActive(presetId)) {
    model.value = undefined
  } else {
    model.value = [0, preset.maxValue]
  }
}
</script>
